import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MixedPalletBuilder from '../components/MixedPalletBuilder';
import './CargoEntry.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function adaptiveChWidth(value, minChars = 2) {
  const length = String(value ?? '').length;
  return `${Math.max(minChars, length + 1)}ch`;
}

const EMPTY_CARGO_FORM = {
  item_number: '',
  material_description: '',
  dimensions: '',
  quantity_pieces: '',
  net_weight_kg: '',
  gross_weight_kg: '',
  pallet_count: '',
  pallet_numbers: [],
};

function normalizePalletSerial(value) {
  return String(value ?? '').trim();
}

function findDuplicatePalletSerials(candidateSerials, existingCargoItems = []) {
  const filled = (candidateSerials || [])
    .map(normalizePalletSerial)
    .filter(Boolean);

  const withinFormCounts = {};
  filled.forEach((serial) => {
    withinFormCounts[serial] = (withinFormCounts[serial] || 0) + 1;
  });
  const withinForm = Object.keys(withinFormCounts).filter((serial) => withinFormCounts[serial] > 1);

  const usedElsewhere = {};
  (existingCargoItems || []).forEach((item) => {
    (item.pallet_numbers || []).forEach((serial) => {
      const key = normalizePalletSerial(serial);
      if (!key) return;
      if (!usedElsewhere[key]) {
        usedElsewhere[key] = [];
      }
      usedElsewhere[key].push(item.material_description || `Item #${item.item_number || item.cargo_id}`);
    });
  });

  const againstPackingList = [...new Set(filled)].filter((serial) => usedElsewhere[serial]);

  return {
    withinForm,
    againstPackingList,
    usedElsewhere,
    hasDuplicates: withinForm.length > 0 || againstPackingList.length > 0,
  };
}

export default function CargoEntry({ token }) {
  const { packingListId } = useParams();
  const navigate = useNavigate();

  const [packingList, setPackingList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [enablingMixed, setEnablingMixed] = useState(false);

  const [cargoForm, setCargoForm] = useState({ ...EMPTY_CARGO_FORM });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showDeletePackingList, setShowDeletePackingList] = useState(false);
  const [duplicateSerialWarning, setDuplicateSerialWarning] = useState(null);

  useEffect(() => {
    fetchPackingList();
  }, [packingListId]);

  const isMixed = packingList?.pallet_mode === 'mixed';

  useEffect(() => {
    if (isMixed || !packingList) {
      setDuplicateSerialWarning(null);
      return;
    }
    const check = findDuplicatePalletSerials(
      cargoForm.pallet_numbers,
      packingList.cargo_items || []
    );
    setDuplicateSerialWarning(check.hasDuplicates ? check : null);
  }, [cargoForm.pallet_numbers, packingList, isMixed]);

  const fetchPackingList = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/packing-lists/${packingListId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load packing list');
      }

      const data = await response.json();
      setPackingList(data);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resizePalletNumbers = (palletNumbers, quantity) => {
    const existing = palletNumbers || [];
    return Array.from({ length: quantity }, (_, index) => existing[index] || '');
  };

  const handleQuantityChange = (e) => {
    const quantity = parseInt(e.target.value, 10) || 0;
    setCargoForm((prev) => ({
      ...prev,
      quantity_pieces: e.target.value,
      pallet_numbers: resizePalletNumbers(prev.pallet_numbers, quantity),
    }));
  };

  const handlePalletNumberChange = (index, value) => {
    setCargoForm((prev) => ({
      ...prev,
      pallet_numbers: prev.pallet_numbers.map((num, i) => (i === index ? value : num)),
    }));
  };

  const handleCargoFormChange = (e) => {
    const { name, value } = e.target;
    setCargoForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const buildCargoPayload = (form) => {
    if (isMixed) {
      return {
        item_number: form.item_number,
        material_description: form.material_description,
        dimensions: form.dimensions,
        quantity_pieces: form.quantity_pieces,
        net_weight_kg: form.net_weight_kg,
        gross_weight_kg: form.gross_weight_kg,
      };
    }
    return form;
  };

  const isDuplicateSerial = (value) => {
    const serial = normalizePalletSerial(value);
    if (!serial || !duplicateSerialWarning) {
      return false;
    }
    return (
      duplicateSerialWarning.withinForm.includes(serial) ||
      duplicateSerialWarning.againstPackingList.includes(serial)
    );
  };

  const handleAddCargo = async () => {
    try {
      if (!cargoForm.material_description) {
        setError('Material description is required');
        return;
      }

      if (duplicateSerialWarning?.hasDuplicates) {
        const confirmed = window.confirm(
          'Duplicate pallet serial numbers were detected. Continue and add this cargo item as entered?'
        );
        if (!confirmed) {
          return;
        }
      }

      const response = await fetch(`${API_URL}/packing-lists/${packingListId}/cargo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(buildCargoPayload(cargoForm)),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add cargo item');
      }

      setSuccess('Cargo item added successfully');
      setError('');
      setCargoForm({ ...EMPTY_CARGO_FORM });

      setTimeout(() => {
        fetchPackingList();
        setSuccess('');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditStart = (item) => {
    if (isMixed) {
      setEditingId(item.cargo_id);
      setEditForm({
        item_number: item.item_number || '',
        material_description: item.material_description || '',
        dimensions: item.dimensions || '',
        quantity_pieces: item.quantity_pieces || '',
        net_weight_kg: item.net_weight_kg || '',
        gross_weight_kg: item.gross_weight_kg || '',
      });
      return;
    }

    const palletTotal = parseInt(item.quantity_pieces, 10) || (item.pallet_numbers || []).length || 0;
    setEditingId(item.cargo_id);
    setEditForm({
      item_number: item.item_number || '',
      material_description: item.material_description || '',
      dimensions: item.dimensions || '',
      quantity_pieces: item.quantity_pieces || '',
      net_weight_kg: item.net_weight_kg || '',
      gross_weight_kg: item.gross_weight_kg || '',
      pallet_count: item.pallet_count || '',
      pallet_numbers: resizePalletNumbers(item.pallet_numbers, palletTotal),
    });
  };

  const handleEditQuantityChange = (e) => {
    const quantity = parseInt(e.target.value, 10) || 0;
    setEditForm((prev) => ({
      ...prev,
      quantity_pieces: e.target.value,
      pallet_numbers: resizePalletNumbers(prev.pallet_numbers, quantity),
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditPalletChange = (index, value) => {
    setEditForm((prev) => ({
      ...prev,
      pallet_numbers: prev.pallet_numbers.map((num, i) => (i === index ? value : num)),
    }));
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${API_URL}/cargo/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(isMixed ? buildCargoPayload(editForm) : editForm),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update cargo item');
      }

      setSuccess('Cargo item updated successfully');
      setEditingId(null);
      setError('');

      setTimeout(() => {
        fetchPackingList();
        setSuccess('');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteCargo = async (cargo_id) => {
    try {
      const response = await fetch(`${API_URL}/cargo/${cargo_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to delete cargo item');
      }

      setSuccess('Cargo item deleted successfully');
      setShowDeleteConfirm(null);
      setError('');

      setTimeout(() => {
        fetchPackingList();
        setSuccess('');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeletePackingList = async () => {
    try {
      const response = await fetch(`${API_URL}/packing-lists/${packingListId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to delete packing list');
      }

      setSuccess('Packing list deleted successfully');
      setTimeout(() => {
        navigate('/packing-lists');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEnableMixedMode = async () => {
    try {
      setEnablingMixed(true);
      const response = await fetch(`${API_URL}/packing-lists/${packingListId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ pallet_mode: 'mixed' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Could not enable mixed pallet mode');
      }
      setSuccess('Mixed pallet mode enabled');
      setError('');
      await fetchPackingList();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setEnablingMixed(false);
    }
  };

  const showEnableMixed =
    !isMixed &&
    !(packingList?.cargo_items || []).some((item) => (item.pallet_numbers || []).length > 0);

  if (loading) {
    return <div className="loading">Loading packing list...</div>;
  }

  if (!packingList) {
    return <div className="error">Packing list not found</div>;
  }

  return (
    <div className="cargo-entry-container">
      <div className="cargo-header">
        <div>
          <h1>Cargo Entry</h1>
          <p>
            Shesha Note: {packingList.order_number || 'N/A'} | Invoice: {packingList.invoice_number || 'N/A'}
          </p>
          <div className="mode-badges">
            <span className={`mode-badge ${isMixed ? 'mode-mixed' : 'mode-simple'}`}>
              {isMixed ? 'Mixed pallets' : 'Simple pallets'}
            </span>
          </div>
        </div>
        <div className="header-actions">
          {showEnableMixed && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleEnableMixedMode}
              disabled={enablingMixed}
            >
              {enablingMixed ? 'Enabling…' : 'Switch to mixed pallets'}
            </button>
          )}
          <button
            className="btn btn-danger"
            onClick={() => setShowDeletePackingList(true)}
            title="Delete entire packing list"
          >
            Delete Packing List
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showDeletePackingList && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Delete Packing List?</h3>
            <p>This will delete the entire packing list and all cargo items. This cannot be undone.</p>
            <div className="modal-buttons">
              <button className="btn btn-danger" onClick={handleDeletePackingList}>
                Delete
              </button>
              <button className="btn btn-secondary" onClick={() => setShowDeletePackingList(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="cargo-form-section">
        <h2>Add New Cargo Item</h2>
        {isMixed && (
          <p className="form-mode-hint">
            First enter each <strong>material</strong> and its <strong>total piece count</strong> for the whole shipment (supplier summary).
            Then use <strong>item numbers</strong> below to recreate the supplier packing list — each item number is one pallet with its materials and piece counts.
          </p>
        )}

        <div className="form-row">
          {!isMixed && (
            <div className="form-group">
              <label>Item Number</label>
              <input
                type="number"
                name="item_number"
                value={cargoForm.item_number}
                onChange={handleCargoFormChange}
                placeholder="e.g., 1"
              />
            </div>
          )}
          <div className={`form-group ${isMixed ? 'form-group-wide' : ''}`}>
            <label>Material Description *</label>
            <input
              type="text"
              name="material_description"
              value={cargoForm.material_description}
              onChange={handleCargoFormChange}
              placeholder="e.g., Refractory Bricks"
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Dimensions</label>
            <input
              type="text"
              name="dimensions"
              value={cargoForm.dimensions}
              onChange={handleCargoFormChange}
              placeholder="e.g., 10x20x30 cm"
            />
          </div>
          <div className="form-group">
            <label>{isMixed ? 'Total pieces *' : 'Number of pallets'}</label>
            <input
              type="number"
              name="quantity_pieces"
              value={cargoForm.quantity_pieces}
              onChange={isMixed ? handleCargoFormChange : handleQuantityChange}
              placeholder="0"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Net Weight (kg)</label>
            <input
              type="number"
              name="net_weight_kg"
              value={cargoForm.net_weight_kg}
              onChange={handleCargoFormChange}
              placeholder="0"
            />
          </div>
          <div className="form-group">
            <label>Gross Weight (kg)</label>
            <input
              type="number"
              name="gross_weight_kg"
              value={cargoForm.gross_weight_kg}
              onChange={handleCargoFormChange}
              placeholder="0"
            />
          </div>
        </div>

        {!isMixed && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Number of pcs/pallet</label>
                <input
                  type="number"
                  name="pallet_count"
                  value={cargoForm.pallet_count}
                  onChange={handleCargoFormChange}
                  placeholder="0"
                />
              </div>
            </div>

            {cargoForm.quantity_pieces && parseInt(cargoForm.quantity_pieces, 10) > 0 && (
              <div className="pallet-numbers-section">
                <label>Pallet Serial Numbers ({cargoForm.quantity_pieces} required)</label>
                <div className="pallet-inputs">
                  {cargoForm.pallet_numbers.map((num, idx) => (
                    <input
                      key={idx}
                      type="text"
                      value={num}
                      onChange={(e) => handlePalletNumberChange(idx, e.target.value)}
                      placeholder={`Pallet ${idx + 1}`}
                      className={isDuplicateSerial(num) ? 'pallet-serial-duplicate' : ''}
                      aria-invalid={isDuplicateSerial(num)}
                    />
                  ))}
                </div>
                {duplicateSerialWarning && (
                  <div className="duplicate-serial-warning" role="alert">
                    <strong>Duplicate pallet serial numbers detected.</strong>
                    <span> Fix the highlighted fields, or continue and add the cargo item as entered.</span>
                    {duplicateSerialWarning.withinForm.length > 0 && (
                      <div>
                        Repeated in this form: {duplicateSerialWarning.withinForm.join(', ')}
                      </div>
                    )}
                    {duplicateSerialWarning.againstPackingList.length > 0 && (
                      <ul>
                        {duplicateSerialWarning.againstPackingList.map((serial) => (
                          <li key={serial}>
                            {serial}
                            {' — already on '}
                            {(duplicateSerialWarning.usedElsewhere[serial] || []).join('; ')}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <button className="btn btn-primary" onClick={handleAddCargo}>
          Add Cargo Item
        </button>
      </div>

      <div className="cargo-items-section">
        <h2>{isMixed ? 'Materials (shipment totals)' : 'Cargo Items'} ({packingList.cargo_items?.length || 0})</h2>

        {!packingList.cargo_items || packingList.cargo_items.length === 0 ? (
          <div className="no-items">No cargo items yet</div>
        ) : (
          <div className="cargo-table-wrapper">
            <table className={`cargo-table ${isMixed ? 'cargo-table-mixed' : ''}`}>
              <thead>
                <tr>
                  {!isMixed && <th>Item #</th>}
                  <th>Material description</th>
                  <th>Total pieces</th>
                  {isMixed && (
                    <>
                      <th>On item #s</th>
                      <th>Remaining</th>
                    </>
                  )}
                  {!isMixed && <th>Pieces Remaining</th>}
                  <th>Net Wt (kg)</th>
                  <th>Gross Wt (kg)</th>
                  {!isMixed && (
                    <>
                      <th>Pcs/pallet</th>
                      <th>Serial #s</th>
                    </>
                  )}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {packingList.cargo_items.map((item) => {
                  const alloc = (packingList.mixed_pallet_allocation || []).find(
                    (row) => row.cargo_id === item.cargo_id
                  );
                  return editingId === item.cargo_id ? (
                    <tr key={item.cargo_id} className="edit-row">
                      {!isMixed && (
                        <td>
                          <input
                            className="edit-cell-input adaptive-number-input"
                            type="number"
                            name="item_number"
                            style={{ width: adaptiveChWidth(editForm.item_number) }}
                            value={editForm.item_number}
                            onChange={handleEditChange}
                            aria-label="Item number"
                          />
                        </td>
                      )}
                      <td>
                        <input
                          className="edit-cell-input"
                          type="text"
                          name="material_description"
                          value={editForm.material_description}
                          onChange={handleEditChange}
                          aria-label="Material description"
                        />
                      </td>
                      <td>
                        <input
                          className="edit-cell-input"
                          type="number"
                          name="quantity_pieces"
                          value={editForm.quantity_pieces}
                          onChange={isMixed ? handleEditChange : handleEditQuantityChange}
                          aria-label={isMixed ? 'Total pieces' : 'Number of pallets'}
                        />
                      </td>
                      {isMixed && (
                        <>
                          <td>{alloc?.on_pallets ?? 0}</td>
                          <td className={alloc?.balanced ? 'remaining-qty' : 'remaining-pending'}>
                            {alloc?.remaining ?? item.quantity_pieces}
                          </td>
                        </>
                      )}
                      {!isMixed && (
                        <td className="remaining-qty">
                          {item.inventory?.quantity_remaining ?? item.quantity_pieces ?? '-'}
                        </td>
                      )}
                      <td>
                        <input
                          className="edit-cell-input"
                          type="number"
                          name="net_weight_kg"
                          value={editForm.net_weight_kg}
                          onChange={handleEditChange}
                          aria-label="Net weight"
                        />
                      </td>
                      <td>
                        <input
                          className="edit-cell-input"
                          type="number"
                          name="gross_weight_kg"
                          value={editForm.gross_weight_kg}
                          onChange={handleEditChange}
                          aria-label="Gross weight"
                        />
                      </td>
                      {!isMixed && (
                        <>
                          <td>
                            <input
                              className="edit-cell-input"
                              type="number"
                              name="pallet_count"
                              value={editForm.pallet_count}
                              onChange={handleEditChange}
                              aria-label="Pieces per pallet"
                            />
                          </td>
                          <td>
                            {editForm.quantity_pieces && parseInt(editForm.quantity_pieces, 10) > 0 ? (
                              <div className="edit-serial-inputs">
                                {editForm.pallet_numbers.map((num, idx) => (
                                  <input
                                    key={idx}
                                    className="edit-cell-input"
                                    type="text"
                                    value={num}
                                    onChange={(e) => handleEditPalletChange(idx, e.target.value)}
                                    placeholder={`Pallet ${idx + 1}`}
                                    aria-label={`Pallet ${idx + 1} serial number`}
                                  />
                                ))}
                              </div>
                            ) : (
                              <span className="edit-empty-serials">—</span>
                            )}
                          </td>
                        </>
                      )}
                      <td className="actions edit-actions">
                        <button className="btn btn-primary btn-sm" onClick={handleSaveEdit}>
                          Save
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)}>
                          Cancel
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={item.cargo_id}>
                      {!isMixed && <td>{item.item_number || '-'}</td>}
                      <td>{item.material_description}</td>
                      <td>{item.quantity_pieces}</td>
                      {isMixed && (
                        <>
                          <td>{alloc?.on_pallets ?? 0}</td>
                          <td className={alloc?.balanced ? 'remaining-qty' : 'remaining-pending'}>
                            {alloc?.remaining ?? item.quantity_pieces}
                          </td>
                        </>
                      )}
                      {!isMixed && (
                        <td className="remaining-qty">
                          {item.inventory?.quantity_remaining ?? item.quantity_pieces}
                        </td>
                      )}
                      <td>{item.net_weight_kg || '-'}</td>
                      <td>{item.gross_weight_kg || '-'}</td>
                      {!isMixed && (
                        <>
                          <td>{item.pallet_count || '-'}</td>
                          <td>{item.pallet_numbers?.join(', ') || 'N/A'}</td>
                        </>
                      )}
                      <td className="actions">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditStart(item)}
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => setShowDeleteConfirm(item.cargo_id)}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {showDeleteConfirm && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Delete Cargo Item?</h3>
              <p>This cargo item will be permanently deleted.</p>
              <div className="modal-buttons">
                <button className="btn btn-danger" onClick={() => handleDeleteCargo(showDeleteConfirm)}>
                  Delete
                </button>
                <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {isMixed && (
        <MixedPalletBuilder
          packingListId={packingListId}
          token={token}
          cargoItems={packingList.cargo_items || []}
          pallets={packingList.pallets || []}
          allocation={packingList.mixed_pallet_allocation || []}
          validationErrors={packingList.mixed_pallet_validation || []}
          onRefresh={fetchPackingList}
          onError={setError}
          onSuccess={(message) => {
            setSuccess(message);
            setTimeout(() => setSuccess(''), 2000);
          }}
        />
      )}

      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={() => navigate('/packing-lists')}>
          Back to List
        </button>
      </div>
    </div>
  );
}
